import { useCallback, useEffect, useState } from 'react'
import { fetchData } from '../../../global/utils/fetch'
import {
  getAdminChatLogs,
  getAdminSessionsForUser,
  getAdminSessionDetail,
} from '../../api'
import styles from './ConversationLogsContent.module.css'

interface TechnicianEntry {
  createdBy: string
  sessionCount: number
  lastActivity: string
}

interface SessionEntry {
  objectId: string
  createdBy: string | null
  created: string
  updated: string
  messageCount: number
  lastMessage: string | null
  lastMessageAt: string | null
}

interface MessageEntry {
  objectId: string
  role: 'user' | 'assistant'
  content: string
  created: string
}

interface SessionDetail {
  session: { objectId: string; createdBy: string | null; created: string }
  messages: MessageEntry[]
}

function ConversationLogsContent() {
  const [technicians, setTechnicians] = useState<TechnicianEntry[]>([])
  const [sessions, setSessions] = useState<SessionEntry[]>([])
  const [conversation, setConversation] = useState<SessionDetail | null>(null)
  const [selectedTechnician, setSelectedTechnician] = useState<string | null>(null)
  const [selectedSession, setSelectedSession] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const loadTechnicians = useCallback(async () => {
    setIsLoading(true)
    try {
      const data = await fetchData<TechnicianEntry[]>(getAdminChatLogs())
      setTechnicians(data)
    } catch (error) {
      console.error('Failed to load technicians:', error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadTechnicians()
  }, [loadTechnicians])

  async function handleSelectTechnician(createdBy: string) {
    setSelectedTechnician(createdBy)
    setSelectedSession(null)
    setConversation(null)
    setIsLoading(true)
    try {
      const data = await fetchData<SessionEntry[]>(
        getAdminSessionsForUser(createdBy),
      )
      setSessions(data)
    } catch (error) {
      console.error('Failed to load sessions:', error)
    } finally {
      setIsLoading(false)
    }
  }

  async function handleSelectSession(sessionObjectId: string) {
    if (!selectedTechnician) return
    setSelectedSession(sessionObjectId)
    setIsLoading(true)
    try {
      const data = await fetchData<SessionDetail>(
        getAdminSessionDetail(selectedTechnician, sessionObjectId),
      )
      setConversation(data)
    } catch (error) {
      console.error('Failed to load conversation:', error)
    } finally {
      setIsLoading(false)
    }
  }

  function handleBack() {
    if (conversation) {
      setConversation(null)
      setSelectedSession(null)
    } else if (selectedTechnician) {
      setSelectedTechnician(null)
      setSessions([])
    }
  }

  function formatDate(dateStr: string) {
    const date = new Date(dateStr)
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  // Conversation detail view
  if (conversation && selectedTechnician) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <button
            className={styles.backButton}
            onClick={handleBack}
          >
            Back to Sessions
          </button>
          <span className={styles.headerTitle}>
            {selectedTechnician} - {formatDate(conversation.session.created)}
          </span>
        </div>
        <div className={styles.conversationView}>
          {conversation.messages.map((msg) => (
            <div
              key={msg.objectId}
              className={
                msg.role === 'user' ? styles.userMessage : styles.assistantMessage
              }
            >
              <div className={styles.messageHeader}>
                <span className={styles.roleLabel}>
                  {msg.role === 'user' ? 'Technician' : 'AI Assistant'}
                </span>
                <span className={styles.messageTime}>
                  {formatDate(msg.created)}
                </span>
              </div>
              <div className={styles.messageContent}>{msg.content}</div>
            </div>
          ))}
          {conversation.messages.length === 0 && (
            <div className={styles.emptyState}>No messages in this session.</div>
          )}
        </div>
      </div>
    )
  }

  // Sessions list view
  if (selectedTechnician) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <button
            className={styles.backButton}
            onClick={handleBack}
          >
            Back to Technicians
          </button>
          <span className={styles.headerTitle}>{selectedTechnician}</span>
        </div>
        {isLoading ? (
          <div className={styles.loadingState}>Loading sessions...</div>
        ) : (
          <div className={styles.listView}>
            {sessions.map((session) => (
              <div
                key={session.objectId}
                className={`${styles.listItem} ${
                  selectedSession === session.objectId ? styles.listItemActive : ''
                }`}
                onClick={() => handleSelectSession(session.objectId)}
              >
                <div className={styles.listItemHeader}>
                  <span className={styles.listItemTitle}>
                    {formatDate(session.created)}
                  </span>
                  <span className={styles.listItemMeta}>
                    {session.messageCount} messages
                  </span>
                </div>
                {session.lastMessage && (
                  <div className={styles.listItemPreview}>
                    {session.lastMessage}
                  </div>
                )}
              </div>
            ))}
            {sessions.length === 0 && (
              <div className={styles.emptyState}>No sessions found.</div>
            )}
          </div>
        )}
      </div>
    )
  }

  // Technicians list view (default)
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.headerTitle}>Conversation Logs</span>
        <button
          className={styles.refreshButton}
          onClick={loadTechnicians}
        >
          Refresh
        </button>
      </div>
      {isLoading ? (
        <div className={styles.loadingState}>Loading...</div>
      ) : (
        <div className={styles.listView}>
          {technicians.map((tech) => (
            <div
              key={tech.createdBy}
              className={styles.listItem}
              onClick={() => handleSelectTechnician(tech.createdBy)}
            >
              <div className={styles.listItemHeader}>
                <span className={styles.listItemTitle}>{tech.createdBy}</span>
                <span className={styles.listItemMeta}>
                  {tech.sessionCount} session{tech.sessionCount !== 1 ? 's' : ''}
                </span>
              </div>
              <div className={styles.listItemPreview}>
                Last active: {formatDate(tech.lastActivity)}
              </div>
            </div>
          ))}
          {technicians.length === 0 && (
            <div className={styles.emptyState}>
              No conversation logs yet. Logs will appear here once technicians start
              using the AI Assistant.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ConversationLogsContent
